import { useTranslation } from 'react-i18next';
import { Logo } from '../../components/Logo';
import type { PendingSign, PreparedTxPreview, SignPreview, TypedDataPayload, UserOpPreview, WCRequestPreview } from '../../app/state';

export function ConfirmScreen({
  address,
  pending,
  onApprove,
  onReject,
}: {
  address: string;
  pending: PendingSign;
  onApprove: () => Promise<void> | void;
  onReject: () => void;
}) {
  const { t } = useTranslation();
  const short = address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
  const network = ('network' in pending && pending.network) ? pending.network : undefined;
  const curve = ('curve' in pending && pending.curve) ? pending.curve : 'secp256k1';
  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <Logo variant="mark" size={36} />
          </div>
          <h1 className="text-lg font-semibold tracking-tightish text-ink-primary">
            {t('iframe.confirm.title')}
          </h1>
          <div className="mt-2 flex items-center justify-center gap-2">
            <span className="rounded bg-bit2me/10 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase text-bit2me">
              {network ?? curve}
            </span>
          </div>
          <p className="mt-2 break-all font-mono text-[11px] text-ink-tertiary">{short}</p>
        </div>

        {pending.kind === 'message' && (
          <MessagePreview label={t('iframe.confirm.messageLabel')} message={pending.message} />
        )}
        {pending.kind === 'typedData' && <TypedDataPreview td={pending.typedData} />}
        {pending.kind === 'rawTx' && (
          <RawTxPreview tx={pending.tx} preview={pending.preview} />
        )}
        {pending.kind === 'userOp' && (
          <UserOpPreviewView op={pending.op} preview={pending.preview} />
        )}
        {pending.kind === 'wc' && (
          <WCRequestPreviewView wc={pending.wc} />
        )}

        <div className="space-y-2">
          <button
            onClick={() => {
              // Diagnostic: surface a click breadcrumb to the parent + console
              // so integrators can confirm the iframe's React handler did fire.
              try {
                console.log('[sigil-iframe] approve clicked', {
                  kind: pending.kind,
                  requestId: pending.requestId,
                });
                window.parent?.postMessage(
                  { type: 'iframe_debug', event: 'approve_clicked', kind: pending.kind, requestId: pending.requestId },
                  '*',
                );
              } catch { /* debug only — never break the click */ }
              void onApprove();
            }}
            className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-bit2me px-4 text-sm font-medium text-white transition-colors hover:bg-bit2me-mid"
          >
            {t('iframe.confirm.approve')}
          </button>
          <button
            onClick={onReject}
            className="inline-flex h-11 w-full items-center justify-center rounded-lg border border-line bg-surface-white px-4 text-sm font-medium text-ink-primary transition-colors hover:border-ink-primary"
          >
            {t('iframe.confirm.reject')}
          </button>
        </div>
      </div>
    </main>
  );
}

function MessagePreview({ label, message }: { label: string; message: string }) {
  return (
    <div className="mb-6 rounded-lg border border-line-soft bg-surface-soft p-4">
      <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">{label}</p>
      <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-ink-primary">
{message}
      </pre>
    </div>
  );
}

// Raw EVM tx preview: render the prepared transaction so the user knows
// exactly which chain, which contract, how much value, and what gas
// budget they're approving. Unlike executeBatch this is NOT sponsored —
// the user is paying gas from their EOA, so we surface the max cost
// estimate prominently.
function RawTxPreview({ tx, preview }: { tx: PreparedTxPreview; preview?: SignPreview }) {
  const title = preview?.title ?? 'Send transaction';
  const maxCostWei = mulHex(tx.gasLimit, tx.maxFeePerGas);
  const maxCostEth = formatWei(maxCostWei);
  const valueEth = formatWei(BigInt(tx.value));
  return (
    <div className="mb-6 space-y-3">
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">Action</p>
        <p className="mt-1 text-sm font-medium text-ink-primary">{title}</p>
        {preview?.description && (
          <p className="mt-1 text-xs text-ink-secondary">{preview.description}</p>
        )}
        {preview?.fields && preview.fields.length > 0 && (
          <div className="mt-3 space-y-1">
            {preview.fields.map((f) => (
              <div key={f.label} className="flex gap-2 font-mono text-[11px]">
                <span className="shrink-0 text-ink-tertiary">{f.label}</span>
                <span className="min-w-0 truncate text-ink-primary" title={f.value}>
                  {f.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">Transaction</p>
        <dl className="mt-2 space-y-1 font-mono text-[11px]">
          <Row k="chain" v={`${tx.chain} (${parseInt(tx.chainId, 16)})`} />
          <Row k="to" v={tx.to} mono />
          <Row k="value" v={`${valueEth} (native)`} />
          <Row k="gas limit" v={String(parseInt(tx.gasLimit, 16))} />
          <Row k="max cost" v={`${maxCostEth} (native)`} />
          <Row k="nonce" v={String(parseInt(tx.nonce, 16))} />
        </dl>
        <p className="mt-3 text-[10px] text-ink-tertiary">
          You pay the gas from your wallet's balance. Sigil does not sponsor raw transactions.
        </p>
      </div>
    </div>
  );
}

// Sponsored 4337 preview. The contrast with RawTxPreview matters: this
// path is Sigil-subsidised (capped at €10/wallet/month), the user pays
// nothing. We surface that explicitly so an integrator A/B-ing the two
// lanes during onboarding can see at a glance which one the wallet just
// used.
function UserOpPreviewView({ op, preview }: { op: UserOpPreview; preview?: SignPreview }) {
  const title = preview?.title ?? 'Send transaction';
  return (
    <div className="mb-6 space-y-3">
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">Action</p>
        <p className="mt-1 text-sm font-medium text-ink-primary">{title}</p>
        {preview?.description && (
          <p className="mt-1 text-xs text-ink-secondary">{preview.description}</p>
        )}
        {preview?.fields && preview.fields.length > 0 && (
          <div className="mt-3 space-y-1">
            {preview.fields.map((f) => (
              <div key={f.label} className="flex gap-2 font-mono text-[11px]">
                <span className="shrink-0 text-ink-tertiary">{f.label}</span>
                <span className="min-w-0 truncate text-ink-primary" title={f.value}>
                  {f.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">Smart account</p>
        <dl className="mt-2 space-y-1 font-mono text-[11px]">
          <Row k="chain" v={op.chain} />
          <Row k="from" v={op.sender} mono />
          {op.calls.map((c, i) => (
            <Row key={i} k={op.calls.length > 1 ? `to ${i + 1}` : 'to'} v={c.to} mono />
          ))}
        </dl>
        <p className="mt-3 text-[10px] text-ink-tertiary">
          Gas sponsored by Sigil.
        </p>
      </div>
    </div>
  );
}

// WalletConnect request preview. Three subshapes:
//   - personal_sign: show the readable message
//   - eth_sign: show "Sign 32-byte digest" with the digest hex
//   - eth_signTypedData_v4: same as eth_sign (typed data already
//     hashed by the sidecar — we surface the digest, not the
//     original typed data, so the user only confirms what they
//     actually sign)
function WCRequestPreviewView({ wc }: { wc: WCRequestPreview }) {
  return (
    <div className="mb-6 space-y-3">
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">
          WalletConnect — {wc.method}
        </p>
        {wc.peerName && (
          <p className="mt-1 text-sm font-medium text-ink-primary">{wc.peerName}</p>
        )}
        <p className="mt-2 font-mono text-[10px] text-ink-tertiary">
          topic {wc.topic.slice(0, 12)}…
        </p>
      </div>
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        {wc.method === 'personal_sign' ? (
          <>
            <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">
              Message
            </p>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-ink-primary">
{wc.message ?? ''}
            </pre>
          </>
        ) : (
          <>
            <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">
              Digest (32 bytes)
            </p>
            <p className="mt-2 break-all font-mono text-[11px] text-ink-primary">
              {wc.digestHex ?? ''}
            </p>
            <p className="mt-3 text-[10px] text-ink-tertiary">
              Signing a pre-computed digest. The dApp generated this digest
              from {wc.method === 'eth_signTypedData_v4' ? 'EIP-712 typed data' : 'a raw hash'};
              confirm that you trust the dApp to have produced the right value.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-ink-tertiary">{k}</dt>
      <dd className={`min-w-0 truncate text-ink-primary ${mono ? 'break-all' : ''}`} title={v}>
        {v}
      </dd>
    </div>
  );
}

function mulHex(aHex: string, bHex: string): bigint {
  return BigInt(aHex) * BigInt(bHex);
}

// formatWei renders a wei amount in ETH-scaled form with 6-decimal precision.
// "native" because the chain might not actually be Ethereum; the symbol is
// chain-dependent and we don't want to mislabel ARB vs ETH. The unit text is
// added by the caller.
function formatWei(wei: bigint): string {
  const negative = wei < 0n;
  const abs = negative ? -wei : wei;
  const whole = abs / 10n ** 18n;
  const frac = abs % 10n ** 18n;
  const fracStr = frac.toString().padStart(18, '0').slice(0, 6).replace(/0+$/, '');
  const sign = negative ? '-' : '';
  return fracStr ? `${sign}${whole}.${fracStr}` : `${sign}${whole}`;
}

// EIP-712 preview: render the structured data so the user can see what they
// approve, not an opaque digest. Hierarchy: domain (issuer) → primaryType
// (action) → fields (parameters).
function TypedDataPreview({ td }: { td: TypedDataPayload }) {
  return (
    <div className="mb-6 space-y-3">
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">Issued by</p>
        <p className="mt-1 truncate text-sm font-medium text-ink-primary">
          {String(td.domain.name ?? '—')}
        </p>
        {typeof td.domain.chainId !== 'undefined' && (
          <p className="mt-0.5 font-mono text-[11px] text-ink-tertiary">
            chain {String(td.domain.chainId)}
          </p>
        )}
        {typeof td.domain.verifyingContract === 'string' && (
          <p className="mt-0.5 font-mono text-[11px] text-ink-tertiary break-all">
            {td.domain.verifyingContract}
          </p>
        )}
      </div>
      <div className="rounded-lg border border-line-soft bg-surface-soft p-4">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">Action</p>
        <p className="mt-1 text-sm font-medium text-ink-primary">{td.primaryType}</p>
        <div className="mt-3 space-y-1">
          {Object.entries(td.message).map(([k, v]) => (
            <div key={k} className="flex gap-2 font-mono text-[11px]">
              <span className="shrink-0 text-ink-tertiary">{k}</span>
              <span className="min-w-0 truncate text-ink-primary" title={typeof v === 'object' ? JSON.stringify(v) : String(v)}>
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
